/**
 * Jertscript Parser
 *
 * A recursive descent parser that transforms tokens into an AST.
 * "Honestly, TypeScript could use more croissants." 🥐
 */

import { Token, TokenType } from '../lexer/tokens';
import {
  ParserErrorType,
  getParserErrorMessage,
  formatErrorWithLine,
} from '../errors/messages';
import {
  Program,
  Statement,
  Expression,
  VariableDeclaration,
  FunctionDeclaration,
  IfStatement,
  WhileStatement,
  ForStatement,
  ReturnStatement,
  BlockStatement,
  ExpressionStmt,
  TryCatchStatement,
  BreakStatement,
  ContinueStatement,
  ThrowStatement,
  BinaryExpr,
  UnaryExpr,
  LogicalExpr,
  LiteralExpr,
  IdentifierExpr,
  CallExpr,
  GroupingExpr,
  AssignmentExpr,
  MemberExpr,
  MemberAssignmentExpr,
  ArrayExpr,
  ObjectExpr,
  ObjectProperty,
  NewExpr,
  AwaitExpr,
  ThisExpr,
  createProgram,
  createVariableDeclaration,
  createFunctionDeclaration,
  createIfStatement,
  createWhileStatement,
  createForStatement,
  createReturnStatement,
  createBlockStatement,
  createExpressionStmt,
  createTryCatchStatement,
  createBreakStatement,
  createContinueStatement,
  createThrowStatement,
  createBinaryExpr,
  createUnaryExpr,
  createLogicalExpr,
  createLiteralExpr,
  createIdentifierExpr,
  createCallExpr,
  createGroupingExpr,
  createAssignmentExpr,
  createMemberExpr,
  createMemberAssignmentExpr,
  createArrayExpr,
  createObjectExpr,
  createNewExpr,
  createAwaitExpr,
  createThisExpr,
} from './ast';

/**
 * Parser error with line information
 */
export interface ParserError {
  message: string;
  line: number;
  token: Token;
}

/**
 * Parser class that converts tokens into an AST
 */
export class Parser {
  private tokens: Token[];
  private current = 0;
  private errors: ParserError[] = [];

  constructor(tokens: Token[]) {
    this.tokens = tokens;
  }

  /**
   * Main entry point - parses the token stream into a Program AST
   */
  parse(): Program {
    const statements: Statement[] = [];

    while (!this.isAtEnd()) {
      try {
        const stmt = this.declaration();
        if (stmt) {
          statements.push(stmt);
        }
      } catch (error) {
        // Synchronize on error to continue parsing
        this.synchronize();
      }
    }

    return createProgram(statements);
  }

  /**
   * Returns any parser errors encountered during parsing
   */
  getErrors(): ParserError[] {
    return this.errors;
  }

  // ===========================================================================
  // Helper Methods
  // ===========================================================================

  /**
   * Returns the current token without consuming it
   */
  private peek(): Token {
    return this.tokens[this.current];
  }

  /**
   * Returns the previous token
   */
  private previous(): Token {
    if (this.current === 0) {
      // Return current token if we're at the beginning
      return this.tokens[0];
    }
    return this.tokens[this.current - 1];
  }

  /**
   * Checks if we've reached the end of the token stream
   */
  private isAtEnd(): boolean {
    return this.peek().type === TokenType.EOF;
  }

  /**
   * Consumes and returns the current token
   */
  private advance(): Token {
    if (!this.isAtEnd()) {
      this.current++;
    }
    return this.previous();
  }

  /**
   * Checks if the current token is of the given type
   */
  private check(type: TokenType): boolean {
    if (this.isAtEnd()) return false;
    return this.peek().type === type;
  }

  /**
   * Consumes the current token if it matches any of the given types
   */
  private match(...types: TokenType[]): boolean {
    for (const type of types) {
      if (this.check(type)) {
        this.advance();
        return true;
      }
    }
    return false;
  }

  /**
   * Consumes the current token if it matches the expected type,
   * otherwise throws an error
   */
  private consume(type: TokenType, message: string): Token {
    if (this.check(type)) {
      return this.advance();
    }
    throw this.error(this.peek(), message);
  }

  /**
   * Consumes the current token if it matches the expected type,
   * otherwise throws a sarcastic parser error
   */
  private consumeWithError(
    type: TokenType,
    errorType: ParserErrorType,
    substitutions?: Record<string, string>
  ): Token {
    if (this.check(type)) {
      return this.advance();
    }
    throw this.parserError(this.peek(), errorType, substitutions);
  }

  /**
   * Creates a parser error with a custom message
   */
  private error(token: Token, message: string): Error {
    const formattedMessage = formatErrorWithLine(message, token.line);
    this.errors.push({
      message: formattedMessage,
      line: token.line,
      token,
    });
    return new Error(formattedMessage);
  }

  /**
   * Creates a parser error using the sarcastic error message system
   */
  private parserError(
    token: Token,
    type: ParserErrorType,
    substitutions?: Record<string, string>
  ): Error {
    const message = getParserErrorMessage(type, substitutions);
    return this.error(token, message);
  }

  /**
   * Synchronizes the parser after an error
   * by skipping tokens until we find a statement boundary
   */
  private synchronize(): void {
    this.advance();

    while (!this.isAtEnd()) {
      // If we just passed a semicolon, we're likely at a statement boundary
      if (this.previous().type === TokenType.SEMICOLON) return;

      // Check for statement-starting keywords
      switch (this.peek().type) {
        case TokenType.CROISSANT:
        case TokenType.BAGUETTE:
        case TokenType.FONCTION:
        case TokenType.ASYNCHRONE:
        case TokenType.CLASSE:
        case TokenType.FRANCHEMENT:
        case TokenType.PENDANT:
        case TokenType.POUR:
        case TokenType.RETOURNER:
        case TokenType.MALHEUREUSEMENT:
        case TokenType.IMPORTER:
        case TokenType.EXPORTER:
          return;
      }

      this.advance();
    }
  }

  // ===========================================================================
  // Declaration Parsing
  // ===========================================================================

  /**
   * Parses a declaration (variable, function, or statement)
   */
  private declaration(): Statement | null {
    // Check for async function: asynchrone fonction
    if (this.check(TokenType.ASYNCHRONE)) {
      if (this.checkNext(TokenType.FONCTION)) {
        this.advance(); // consume 'asynchrone'
        return this.functionDeclaration(true);
      }
    }

    if (this.match(TokenType.CROISSANT, TokenType.BAGUETTE)) {
      return this.variableDeclaration();
    }

    if (this.match(TokenType.FONCTION)) {
      return this.functionDeclaration(false);
    }

    return this.statement();
  }

  /**
   * Checks if the next token (after current) is of the given type
   */
  private checkNext(type: TokenType): boolean {
    if (this.current + 1 >= this.tokens.length) return false;
    return this.tokens[this.current + 1].type === type;
  }

  /**
   * Parses a variable declaration: croissant/baguette name = value;
   */
  private variableDeclaration(): VariableDeclaration {
    const kind = this.previous().type === TokenType.CROISSANT ? 'croissant' : 'baguette';
    const name = this.consumeWithError(
      TokenType.IDENTIFIER,
      ParserErrorType.EXPECTED_IDENTIFIER,
      { keyword: kind }
    );

    let initializer: Expression | null = null;
    if (this.match(TokenType.EQUAL)) {
      initializer = this.expression();
    }

    this.consumeWithError(
      TokenType.SEMICOLON,
      ParserErrorType.MISSING_SEMICOLON
    );

    return createVariableDeclaration(kind, name, initializer);
  }

  /**
   * Parses a function declaration: fonction name(params) { body }
   * Note: The 'fonction' keyword should already be consumed before calling this method
   * for regular functions (when isAsync is false), but not for async functions
   * (when isAsync is true, we need to consume 'fonction' here).
   */
  private functionDeclaration(isAsync: boolean): FunctionDeclaration {
    // For async functions, we need to consume the 'fonction' keyword
    // For regular functions, it's already consumed by match() in declaration()
    if (isAsync) {
      this.consume(TokenType.FONCTION,
        "Mon ami, expected 'fonction' keyword here.");
    }

    const name = this.consumeWithError(
      TokenType.IDENTIFIER,
      ParserErrorType.EXPECTED_FUNCTION_NAME
    );

    this.consumeWithError(
      TokenType.LPAREN,
      ParserErrorType.EXPECTED_OPENING_PAREN,
      { name: 'function name' }
    );

    const params: Token[] = [];
    if (!this.check(TokenType.RPAREN)) {
      do {
        params.push(
          this.consumeWithError(
            TokenType.IDENTIFIER,
            ParserErrorType.EXPECTED_PARAMETER_NAME
          )
        );
      } while (this.match(TokenType.COMMA));
    }

    this.consumeWithError(
      TokenType.RPAREN,
      ParserErrorType.MISSING_CLOSING_PAREN
    );

    this.consumeWithError(
      TokenType.LBRACE,
      ParserErrorType.EXPECTED_OPENING_BRACE
    );

    const body = this.blockStatements();

    return createFunctionDeclaration(name, params, body, isAsync);
  }

  // ===========================================================================
  // Statement Parsing
  // ===========================================================================

  /**
   * Parses a statement
   */
  private statement(): Statement {
    if (this.match(TokenType.FRANCHEMENT)) {
      return this.ifStatement();
    }

    if (this.match(TokenType.PENDANT)) {
      return this.whileStatement();
    }

    if (this.match(TokenType.POUR)) {
      return this.forStatement();
    }

    if (this.match(TokenType.RETOURNER)) {
      return this.returnStatement();
    }

    if (this.match(TokenType.CASSER)) {
      return this.breakStatement();
    }

    if (this.match(TokenType.CONTINUER)) {
      return this.continueStatement();
    }

    if (this.match(TokenType.LANCER)) {
      return this.throwStatement();
    }

    if (this.match(TokenType.MALHEUREUSEMENT)) {
      return this.tryCatchStatement();
    }

    if (this.match(TokenType.LBRACE)) {
      return this.block();
    }

    return this.expressionStatement();
  }

  /**
   * Parses an if statement: franchement (condition) { then } sinon { else }
   */
  private ifStatement(): IfStatement {
    const line = this.previous().line;

    this.consumeWithError(
      TokenType.LPAREN,
      ParserErrorType.EXPECTED_OPENING_PAREN,
      { name: 'franchement' }
    );
    const condition = this.expression();
    this.consumeWithError(
      TokenType.RPAREN,
      ParserErrorType.MISSING_CLOSING_PAREN
    );

    const thenBranch = this.statement();

    let elseBranch: Statement | null = null;
    if (this.match(TokenType.SINON)) {
      elseBranch = this.statement();
    }

    return createIfStatement(condition, thenBranch, elseBranch, line);
  }

  /**
   * Parses a while statement: pendant (condition) { body }
   */
  private whileStatement(): WhileStatement {
    const line = this.previous().line;

    this.consumeWithError(
      TokenType.LPAREN,
      ParserErrorType.EXPECTED_OPENING_PAREN,
      { name: 'pendant' }
    );
    const condition = this.expression();
    this.consumeWithError(
      TokenType.RPAREN,
      ParserErrorType.MISSING_CLOSING_PAREN
    );

    const body = this.statement();

    return createWhileStatement(condition, body, line);
  }

  /**
   * Parses a for statement: pour (init; condition; increment) { body }
   */
  private forStatement(): ForStatement {
    const line = this.previous().line;

    this.consumeWithError(
      TokenType.LPAREN,
      ParserErrorType.EXPECTED_OPENING_PAREN,
      { name: 'pour' }
    );

    // Initializer
    let initializer: Statement | null = null;
    if (this.match(TokenType.SEMICOLON)) {
      // No initializer - semicolon already consumed
    } else if (this.match(TokenType.CROISSANT, TokenType.BAGUETTE)) {
      // Variable declaration - includes semicolon in variableDeclaration()
      initializer = this.variableDeclaration();
    } else {
      // Expression initializer - parse expression then consume semicolon
      const expr = this.expression();
      this.consumeWithError(
        TokenType.SEMICOLON,
        ParserErrorType.MISSING_SEMICOLON
      );
      initializer = createExpressionStmt(expr);
    }

    // Condition
    let condition: Expression | null = null;
    if (!this.check(TokenType.SEMICOLON)) {
      condition = this.expression();
    }
    this.consumeWithError(
      TokenType.SEMICOLON,
      ParserErrorType.MISSING_SEMICOLON
    );

    // Increment
    let increment: Expression | null = null;
    if (!this.check(TokenType.RPAREN)) {
      increment = this.expression();
    }
    this.consumeWithError(
      TokenType.RPAREN,
      ParserErrorType.MISSING_CLOSING_PAREN
    );

    const body = this.statement();

    return createForStatement(initializer, condition, increment, body, line);
  }

  /**
   * Parses a return statement: retourner value;
   */
  private returnStatement(): ReturnStatement {
    const keyword = this.previous();

    let value: Expression | null = null;
    if (!this.check(TokenType.SEMICOLON)) {
      value = this.expression();
    }

    this.consumeWithError(
      TokenType.SEMICOLON,
      ParserErrorType.MISSING_SEMICOLON
    );

    return createReturnStatement(keyword, value);
  }

  /**
   * Parses a break statement: casser;
   */
  private breakStatement(): BreakStatement {
    const keyword = this.previous();

    this.consumeWithError(
      TokenType.SEMICOLON,
      ParserErrorType.MISSING_SEMICOLON
    );

    return createBreakStatement(keyword);
  }

  /**
   * Parses a continue statement: continuer;
   */
  private continueStatement(): ContinueStatement {
    const keyword = this.previous();

    this.consumeWithError(
      TokenType.SEMICOLON,
      ParserErrorType.MISSING_SEMICOLON
    );

    return createContinueStatement(keyword);
  }

  /**
   * Parses a throw statement: lancer expression;
   */
  private throwStatement(): ThrowStatement {
    const keyword = this.previous();
    const argument = this.expression();

    this.consumeWithError(
      TokenType.SEMICOLON,
      ParserErrorType.MISSING_SEMICOLON
    );

    return createThrowStatement(keyword, argument);
  }

  /**
   * Parses a block: { statements }
   */
  private block(): BlockStatement {
    const line = this.previous().line;
    const statements = this.blockStatements();
    return createBlockStatement(statements, line);
  }

  /**
   * Parses the statements inside a block (consumes the closing brace)
   */
  private blockStatements(): Statement[] {
    const statements: Statement[] = [];

    while (!this.check(TokenType.RBRACE) && !this.isAtEnd()) {
      const stmt = this.declaration();
      if (stmt) {
        statements.push(stmt);
      }
    }

    this.consumeWithError(
      TokenType.RBRACE,
      ParserErrorType.MISSING_CLOSING_BRACE
    );

    return statements;
  }

  /**
   * Parses a try-catch-finally statement:
   * malheureusement { try } merde (e) { catch } quandmeme { finally }
   */
  private tryCatchStatement(): TryCatchStatement {
    const line = this.previous().line;

    // Parse try block
    this.consumeWithError(
      TokenType.LBRACE,
      ParserErrorType.EXPECTED_OPENING_BRACE
    );
    const tryBlock = createBlockStatement(this.blockStatements(), line);

    // Parse optional catch block: merde (e) { ... }
    let catchParam: Token | null = null;
    let catchBlock: BlockStatement | null = null;
    if (this.match(TokenType.MERDE)) {
      this.consumeWithError(
        TokenType.LPAREN,
        ParserErrorType.EXPECTED_OPENING_PAREN,
        { name: 'merde' }
      );
      catchParam = this.consumeWithError(
        TokenType.IDENTIFIER,
        ParserErrorType.EXPECTED_IDENTIFIER,
        { keyword: 'catch parameter' }
      );
      this.consumeWithError(
        TokenType.RPAREN,
        ParserErrorType.MISSING_CLOSING_PAREN
      );

      this.consumeWithError(
        TokenType.LBRACE,
        ParserErrorType.EXPECTED_OPENING_BRACE
      );
      catchBlock = createBlockStatement(this.blockStatements(), this.previous().line);
    }

    // Parse optional finally block: quandmeme { ... }
    let finallyBlock: BlockStatement | null = null;
    if (this.match(TokenType.QUANDMEME)) {
      this.consumeWithError(
        TokenType.LBRACE,
        ParserErrorType.EXPECTED_OPENING_BRACE
      );
      finallyBlock = createBlockStatement(this.blockStatements(), this.previous().line);
    }

    // Must have at least catch or finally
    if (!catchBlock && !finallyBlock) {
      throw this.error(
        this.peek(),
        "Sacre bleu! You have a 'malheureusement' but no 'merde' or 'quandmeme'. Even the CTO knows you need at least one of them!"
      );
    }

    return createTryCatchStatement(tryBlock, catchParam, catchBlock, finallyBlock, line);
  }

  /**
   * Parses an expression statement
   */
  private expressionStatement(): ExpressionStmt {
    const expr = this.expression();
    this.consumeWithError(
      TokenType.SEMICOLON,
      ParserErrorType.MISSING_SEMICOLON
    );
    return createExpressionStmt(expr);
  }

  // ===========================================================================
  // Expression Parsing (placeholder for PARSER-003)
  // ===========================================================================

  /**
   * Parses an expression
   * Note: Full expression parsing will be implemented in PARSER-003
   */
  private expression(): Expression {
    return this.assignment();
  }

  /**
   * Parses an assignment expression: identifier = value or obj.prop = value
   */
  private assignment(): Expression {
    const expr = this.or();

    if (this.match(TokenType.EQUAL)) {
      const equals = this.previous();
      const value = this.assignment(); // Right-associative

      if (expr.type === 'IdentifierExpr') {
        const name: Token = {
          type: TokenType.IDENTIFIER,
          lexeme: (expr as IdentifierExpr).name,
          literal: null,
          line: expr.line,
        };
        return createAssignmentExpr(name, value);
      }

      // Allow member expression as assignment target (obj.prop = value, arr[i] = value)
      if (expr.type === 'MemberExpr') {
        const memberExpr = expr as MemberExpr;
        return createMemberAssignmentExpr(
          memberExpr.object,
          memberExpr.property,
          memberExpr.computed,
          value,
          expr.line
        );
      }

      throw this.parserError(equals, ParserErrorType.INVALID_ASSIGNMENT_TARGET);
    }

    return expr;
  }

  /**
   * Parses logical OR: expr ou expr
   */
  private or(): Expression {
    let expr = this.and();

    while (this.match(TokenType.OU)) {
      const operator = this.previous();
      const right = this.and();
      expr = createLogicalExpr(expr, operator, right);
    }

    return expr;
  }

  /**
   * Parses logical AND: expr et expr
   */
  private and(): Expression {
    let expr = this.equality();

    while (this.match(TokenType.ET)) {
      const operator = this.previous();
      const right = this.equality();
      expr = createLogicalExpr(expr, operator, right);
    }

    return expr;
  }

  /**
   * Parses equality: expr == expr, expr est expr
   */
  private equality(): Expression {
    let expr = this.comparison();

    while (this.match(TokenType.EQUAL_EQUAL, TokenType.EST, TokenType.NEST_PAS)) {
      const operator = this.previous();
      const right = this.comparison();
      expr = createBinaryExpr(expr, operator, right);
    }

    return expr;
  }

  /**
   * Parses comparison: < > <= >=
   */
  private comparison(): Expression {
    let expr = this.term();

    while (this.match(TokenType.LESS, TokenType.GREATER, TokenType.LESS_EQUAL, TokenType.GREATER_EQUAL)) {
      const operator = this.previous();
      const right = this.term();
      expr = createBinaryExpr(expr, operator, right);
    }

    return expr;
  }

  /**
   * Parses term: + -
   */
  private term(): Expression {
    let expr = this.factor();

    while (this.match(TokenType.PLUS, TokenType.MINUS)) {
      const operator = this.previous();
      const right = this.factor();
      expr = createBinaryExpr(expr, operator, right);
    }

    return expr;
  }

  /**
   * Parses factor: * /
   */
  private factor(): Expression {
    let expr = this.unary();

    while (this.match(TokenType.STAR, TokenType.SLASH)) {
      const operator = this.previous();
      const right = this.unary();
      expr = createBinaryExpr(expr, operator, right);
    }

    return expr;
  }

  /**
   * Parses unary: ! - pas attendre nouveau
   */
  private unary(): Expression {
    if (this.match(TokenType.PAS, TokenType.MINUS)) {
      const operator = this.previous();
      const operand = this.unary();
      return createUnaryExpr(operator, operand);
    }

    // Handle 'attendre' (await)
    if (this.match(TokenType.ATTENDRE)) {
      const line = this.previous().line;
      const argument = this.unary();
      return createAwaitExpr(argument, line);
    }

    // Handle 'nouveau' (new)
    if (this.match(TokenType.NOUVEAU)) {
      return this.newExpression();
    }

    return this.call();
  }

  /**
   * Parses new expression: nouveau Classe(args)
   */
  private newExpression(): NewExpr {
    const line = this.previous().line;

    // Parse the callee (can be an identifier or member expression)
    let callee = this.primary();

    // Handle member access on the callee (e.g., nouveau some.Thing())
    while (this.match(TokenType.DOT)) {
      const property = this.consumeWithError(
        TokenType.IDENTIFIER,
        ParserErrorType.EXPECTED_IDENTIFIER,
        { keyword: 'property name' }
      );
      const propExpr = createIdentifierExpr(property.lexeme, property.line);
      callee = createMemberExpr(callee, propExpr, false, property.line);
    }

    // Parse arguments if present
    let args: Expression[] = [];
    if (this.match(TokenType.LPAREN)) {
      if (!this.check(TokenType.RPAREN)) {
        do {
          args.push(this.expression());
        } while (this.match(TokenType.COMMA));
      }
      this.consumeWithError(
        TokenType.RPAREN,
        ParserErrorType.MISSING_CLOSING_PAREN
      );
    }

    return createNewExpr(callee, args, line);
  }

  /**
   * Parses function calls and member access
   */
  private call(): Expression {
    let expr = this.primary();

    while (true) {
      if (this.match(TokenType.LPAREN)) {
        expr = this.finishCall(expr);
      } else if (this.match(TokenType.DOT)) {
        // Dot member access: obj.property
        const property = this.consumeWithError(
          TokenType.IDENTIFIER,
          ParserErrorType.EXPECTED_IDENTIFIER,
          { keyword: 'property name' }
        );
        const propExpr = createIdentifierExpr(property.lexeme, property.line);
        expr = createMemberExpr(expr, propExpr, false, property.line);
      } else if (this.match(TokenType.LBRACKET)) {
        // Bracket member access: obj[expr]
        const line = this.previous().line;
        const index = this.expression();
        this.consumeWithError(
          TokenType.RBRACKET,
          ParserErrorType.MISSING_CLOSING_BRACE  // Reuse this error for now
        );
        expr = createMemberExpr(expr, index, true, line);
      } else {
        break;
      }
    }

    return expr;
  }

  /**
   * Finishes parsing a function call
   */
  private finishCall(callee: Expression): Expression {
    const args: Expression[] = [];

    if (!this.check(TokenType.RPAREN)) {
      do {
        args.push(this.expression());
      } while (this.match(TokenType.COMMA));
    }

    const paren = this.consumeWithError(
      TokenType.RPAREN,
      ParserErrorType.MISSING_CLOSING_PAREN
    );

    return createCallExpr(callee, args, paren);
  }

  /**
   * Parses primary expressions: literals, identifiers, grouping, arrays, objects
   */
  private primary(): Expression {
    // Boolean literals
    if (this.match(TokenType.OUI)) {
      return createLiteralExpr(true, this.previous().line);
    }

    if (this.match(TokenType.NON)) {
      return createLiteralExpr(false, this.previous().line);
    }

    // Null literal
    if (this.match(TokenType.RIEN)) {
      return createLiteralExpr(null, this.previous().line);
    }

    // Number literal
    if (this.match(TokenType.NUMBER)) {
      return createLiteralExpr(this.previous().literal as number, this.previous().line);
    }

    // String literal
    if (this.match(TokenType.STRING)) {
      return createLiteralExpr(this.previous().literal as string, this.previous().line);
    }

    // 'ceci' (this) keyword
    if (this.match(TokenType.CECI)) {
      return createThisExpr(this.previous());
    }

    // Identifier
    if (this.match(TokenType.IDENTIFIER)) {
      return createIdentifierExpr(this.previous().lexeme, this.previous().line);
    }

    // Grouping
    if (this.match(TokenType.LPAREN)) {
      const line = this.previous().line;
      const expr = this.expression();
      this.consumeWithError(
        TokenType.RPAREN,
        ParserErrorType.MISSING_CLOSING_PAREN
      );
      return createGroupingExpr(expr, line);
    }

    // Array literal: [elem1, elem2, ...]
    if (this.match(TokenType.LBRACKET)) {
      return this.arrayLiteral();
    }

    // Object literal: { key: value, ... }
    if (this.match(TokenType.LBRACE)) {
      return this.objectLiteral();
    }

    throw this.parserError(this.peek(), ParserErrorType.EXPECTED_EXPRESSION);
  }

  /**
   * Parses an array literal: [elem1, elem2, ...]
   */
  private arrayLiteral(): ArrayExpr {
    const line = this.previous().line;
    const elements: Expression[] = [];

    if (!this.check(TokenType.RBRACKET)) {
      do {
        // Allow trailing comma
        if (this.check(TokenType.RBRACKET)) break;
        elements.push(this.expression());
      } while (this.match(TokenType.COMMA));
    }

    this.consumeWithError(
      TokenType.RBRACKET,
      ParserErrorType.MISSING_CLOSING_BRACE  // Reuse this error
    );

    return createArrayExpr(elements, line);
  }

  /**
   * Parses an object literal: { key: value, ... }
   */
  private objectLiteral(): ObjectExpr {
    const line = this.previous().line;
    const properties: ObjectProperty[] = [];

    if (!this.check(TokenType.RBRACE)) {
      do {
        // Allow trailing comma
        if (this.check(TokenType.RBRACE)) break;

        let key: Token;
        let computed = false;

        // Check for computed property key: [expr]: value
        if (this.match(TokenType.LBRACKET)) {
          computed = true;
          const keyExpr = this.expression();
          this.consumeWithError(
            TokenType.RBRACKET,
            ParserErrorType.MISSING_CLOSING_BRACE
          );
          // Create a synthetic token for the key
          key = {
            type: TokenType.IDENTIFIER,
            lexeme: '',  // Will be computed at runtime
            literal: null,
            line: keyExpr.line,
          };
          // Store the key expression - we need to handle this differently
          // For now, use identifier as key and mark as computed
          if (keyExpr.type === 'IdentifierExpr') {
            key.lexeme = (keyExpr as IdentifierExpr).name;
          } else if (keyExpr.type === 'LiteralExpr') {
            key.lexeme = String((keyExpr as LiteralExpr).value);
          }
        } else if (this.match(TokenType.IDENTIFIER)) {
          key = this.previous();
        } else if (this.match(TokenType.STRING)) {
          key = this.previous();
          // Use the string literal value as the key lexeme
          key = { ...key, lexeme: key.literal as string };
        } else if (this.match(TokenType.NUMBER)) {
          key = this.previous();
          // Use the number as the key lexeme
          key = { ...key, lexeme: String(key.literal) };
        } else {
          throw this.parserError(this.peek(), ParserErrorType.EXPECTED_IDENTIFIER, { keyword: 'property key' });
        }

        this.consumeWithError(
          TokenType.COLON,
          ParserErrorType.MISSING_SEMICOLON  // Reuse, not ideal but works
        );

        const value = this.expression();

        properties.push({ key, value, computed });
      } while (this.match(TokenType.COMMA));
    }

    this.consumeWithError(
      TokenType.RBRACE,
      ParserErrorType.MISSING_CLOSING_BRACE
    );

    return createObjectExpr(properties, line);
  }
}

// Re-export AST types for convenience
export * from './ast';

// Re-export parser error types
export { ParserErrorType } from '../errors/messages';
